// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/ai/ContextualExplainDrawer.jsx

import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Button, StatusChip } from "../../design-system/components";
import "./ContextualExplainDrawer.css";

const DEFAULT_PROMPTS = [
  "Explain this risk.",
  "Why does this matter?",
  "Why is this a priority?",
  "What should I do next?",
];

function normalizeRows(value) {
  return Array.isArray(value) ? value : [];
}

function clean(value) {
  return String(value ?? "").trim();
}

function confidenceLabel(value) {
  const normalized = clean(value).toLowerCase();

  if (normalized === "high") return "High confidence";
  if (normalized === "medium") return "Medium confidence";
  if (normalized === "low") return "Low confidence";

  return "Confidence unavailable";
}

function confidenceTone(value) {
  const normalized = clean(value).toLowerCase();

  if (normalized === "high") return "success";
  if (normalized === "medium") return "informational";
  if (normalized === "low") return "warning";

  return "neutral";
}

function sourceLabel(source = {}) {
  const explicit = clean(source?.item_label || source?.label);
  if (explicit) return explicit;

  return clean(source?.id || source?.type || "OASYS source")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function sourceDetail(source = {}) {
  const itemCount = Number(source?.item_count);
  const records = Number(source?.records);

  if (Number.isFinite(itemCount) && itemCount > 0) {
    return `${itemCount} represented`;
  }

  if (Number.isFinite(records) && records > 0) {
    return `${records} loaded`;
  }

  return "Grounded";
}

export default function ContextualExplainDrawer({
  open = false,
  record = null,
  recordLabel = "",
  question = "Explain this risk.",
  card = null,
  metadata = null,
  loading = false,
  error = "",
  themeMode = "light",
  prompts = DEFAULT_PROMPTS,
  contextDescription = "Grounded reasoning for this selected Mission Control record. Deterministic OASYS state remains authoritative.",
  onAsk,
  onClose,
}) {
  const recommendations = useMemo(
    () => normalizeRows(card?.recommended_actions).slice(0, 4),
    [card]
  );

  const evidence = useMemo(
    () =>
      normalizeRows(card?.evidence_used)
        .filter((item) => item && typeof item === "object")
        .slice(0, 12),
    [card]
  );

  const limitations = useMemo(
    () => normalizeRows(card?.known_limitations),
    [card]
  );

  const auditPersisted =
    metadata?.audit_persisted ??
    card?.metadata?.audit_persisted ??
    null;

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && typeof onClose === "function") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  const title =
    clean(recordLabel) ||
    clean(record?.title) ||
    clean(record?.rule_id) ||
    "Selected OASYS record";

  const handleAsk = (prompt) => {
    if (loading || typeof onAsk !== "function") return;
    onAsk(prompt);
  };

  const themeClass =
    String(themeMode || "").trim().toLowerCase() === "dark"
      ? "oasys-theme-dark"
      : "oasys-theme-light";

  return createPortal(
    <div
      className={`contextual-explain ${themeClass}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contextual-explain-title"
    >
      <button
        type="button"
        className="contextual-explain__scrim"
        aria-label="Close explanation"
        onClick={onClose}
      />

      <aside className="contextual-explain__drawer">
        <div className="contextual-explain__header">
          <div className="contextual-explain__identity">
            <span className="contextual-explain__eyebrow">
              OASYS Intelligence · Explain
            </span>

            <h2 id="contextual-explain-title">{title}</h2>

            <p>{contextDescription}</p>
          </div>

          <div className="contextual-explain__header-actions">
            {card ? (
              <StatusChip tone={confidenceTone(card?.confidence)} dot>
                {confidenceLabel(card?.confidence)}
              </StatusChip>
            ) : null}

            {auditPersisted === true ? (
              <StatusChip tone="success">Audited</StatusChip>
            ) : null}

            <button
              type="button"
              onClick={onClose}
              className="contextual-explain__close"
              aria-label="Close contextual explanation"
              title="Close"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>

        <div className="contextual-explain__prompts">
          {prompts.map((prompt) => (
            <Button
              key={prompt}
              variant={question === prompt ? "secondary" : "ghost"}
              size="small"
              disabled={loading}
              onClick={() => handleAsk(prompt)}
              className="contextual-explain__prompt"
            >
              {prompt}
            </Button>
          ))}
        </div>

        <div className="contextual-explain__content" aria-busy={loading}>
          {loading && !card ? (
            <div className="contextual-explain__loading" role="status">
              <span className="contextual-explain__pulse" />

              <div>
                <strong>Reading this record…</strong>
                <span>
                  OASYS is resolving the selected record, related evidence,
                  validation state, linked work, and governed operational context.
                </span>
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="contextual-explain__error" role="alert">
              <strong>Explanation unavailable</strong>
              <span>{error}</span>
            </div>
          ) : null}

          {card ? (
            <>
              <section className="contextual-explain__reasoning">
                <span className="contextual-explain__label">Explanation</span>

                <strong>
                  {card.summary || "Selected record context loaded."}
                </strong>

                {card.reasoning ? <p>{card.reasoning}</p> : null}
              </section>

              {recommendations.length > 0 ? (
                <section className="contextual-explain__actions">
                  <span className="contextual-explain__label">
                    Recommended operator focus
                  </span>

                  <ol>
                    {recommendations.map((action, index) => (
                      <li key={`${String(action)}-${index}`}>
                        <span>{index + 1}</span>
                        <p>{String(action)}</p>
                      </li>
                    ))}
                  </ol>
                </section>
              ) : null}

              <details className="contextual-explain__details">
                <summary>Grounding &amp; transparency</summary>

                <div className="contextual-explain__details-grid">
                  <div>
                    <span className="contextual-explain__label">
                      Grounding
                    </span>

                    {evidence.length > 0 ? (
                      <div className="contextual-explain__sources">
                        {evidence.map((source, index) => (
                          <div
                            key={`${source?.type || "source"}-${
                              source?.id || index
                            }`}
                            className="contextual-explain__source"
                          >
                            <strong>{sourceLabel(source)}</strong>
                            <span>{sourceDetail(source)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="contextual-explain__muted">
                        No source groups were returned for this response.
                      </p>
                    )}
                  </div>

                  <div>
                    <span className="contextual-explain__label">
                      Known limitations
                    </span>

                    {limitations.length > 0 ? (
                      <ul className="contextual-explain__limitations">
                        {limitations.map((limitation, index) => (
                          <li key={`${String(limitation)}-${index}`}>
                            {String(limitation)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="contextual-explain__muted">
                        No additional limitations were reported.
                      </p>
                    )}
                  </div>
                </div>
              </details>
            </>
          ) : null}
        </div>
      </aside>
    </div>,
    document.body
  );
}
